import Flex from "../../../../components/Flex";
import { Box, Paper } from "@mui/material";
import AddSuperBill from "./UpsertSuperBill";
import SuperBillHeader from "./SuperBillHeader";
import PatientDetailsSideSection from "./PatientDetailsSideSection";
import { useGetPatient } from "../../../../queries/use-get-patient-uuid";
import { useGetCodesForSuperBills } from "../../../../queries/billings/use-get-code-for-super-bill";
import { useGetPatientPaymentsByVisit } from "../../../../queries/billings/payments/use-get-patient-payments-by-visit";
import { getXtenantId } from "../../../../utils/utils";
import { useRenderingProviderInfo } from "./use-rendering-provider-info";
import { usePrInvoiceFromClaim } from "./use-pr-invoice-from-claim";
// ECRH-5347: useSearchParams reads the invoice type chosen from the dropdown.
import { useParams, useSearchParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { INVOICE_TYPE, InvoiceType } from "./billing-constant";

const SuperBillingLayout = () => {
  const { patientId, encounterId } = useParams();
  // ECRH-5347: invoice type selected from the "Create Invoice" dropdown. When
  // present, Visit selection becomes mandatory again (Self Pay / Out of Network
  // / Patient Responsibility) and the type is posted with the SuperBill.
  const [searchParams] = useSearchParams();
  const invoiceType =
    (searchParams.get("invoiceType") as InvoiceType | null) ?? undefined;
  // A Patient Responsibility invoice settles a claim, so the header adds a
  // Claim dropdown next to the visit: the user picks the visit first, then the
  // claim billed on it, and everything below (service details, CPT
  // patient-responsibility breakdown, balances) is pre-filled from that claim —
  // identical to the Claims listing → "Create Invoice for PR" flow.
  const isPRInvoice = invoiceType === INVOICE_TYPE.PR;
  const [total, setTotal] = useState(0);
  const patientValidationFormik = useFormik<{
    patient?: string;
    encounter?: { value: string; label: string };
    claim?: { value: string; label: string };
  }>({
    initialValues: {
      patient: patientId || undefined,
      encounter: undefined,
      claim: undefined,
    },
    validationSchema: Yup.object({
      patient: Yup.string().required().label("Patient"),
      // ECRH-5330: Visit made optional by default.
      // ECRH-5347: mandatory again when an invoice type was selected up front,
      // PR included — the claim is only reachable through its visit.
      encounter: invoiceType
        ? Yup.object().required("Visit is required").label("Visit")
        : Yup.object().nullable().label("Visit"),
      // The PR invoice is linked to (and pre-filled from) a claim, so the claim
      // has to be picked before the bill can be saved.
      claim: isPRInvoice
        ? Yup.object().required("Claim is required").label("Claim")
        : Yup.object().nullable().label("Claim"),
    }),
    enableReinitialize: true,
    onSubmit: () => {},
  });

  const selectedClaimId = patientValidationFormik.values.claim?.value;

  // PR: service details / CPT breakdown / balances / provider block, all read
  // from the selected claim.
  const {
    billDetails: prBillDetails,
    renderingProviderInfo: prRenderingProviderInfo,
  } = usePrInvoiceFromClaim(
    isPRInvoice ? selectedClaimId : undefined,
    patientValidationFormik.values.patient
  );

  // Encounter that drives the auto-population of service details / codes and
  // the Visit ID shown on the invoice: from the URL on the "from encounter"
  // flow, otherwise from the Visit dropdown. On PR it is never derived from the
  // claim — the claim is filtered by this visit, so the two always agree.
  const selectedEncounterId =
    encounterId ?? patientValidationFormik.values.encounter?.value;

  const { data: patientData } = useGetPatient(
    patientValidationFormik?.values?.patient
  );

  // A new invoice has no saved record yet, so source the Rendering Provider
  // Details from the selected encounter, matching the Invoice Report. On PR the
  // claim is the source instead, so the encounter call is skipped.
  const { data: encounterData } = useGetCodesForSuperBills({
    appointmentUuid: isPRInvoice ? "" : (selectedEncounterId ?? ""),
    cptBilled: false,
    xTENANTID: getXtenantId(),
  });

  // Consolidated payment summary for the selected visit: advancePayment ->
  // Adv Paid, previousBalance -> Prev. Balance. Auto-populates the Patient
  // Balance section. PR already gets these merged into its claim bill details.
  const { data: paymentByVisit } = useGetPatientPaymentsByVisit({
    patientUuid: patientData?.uuid,
    visitId: selectedEncounterId,
    enabled: !isPRInvoice,
  });

  // Balance-only bill details: the service details / codes are auto-filled from
  // the encounter inside UpsertSuperBill, so we forward just the payment figures.
  const billDetails = useMemo(() => {
    if (isPRInvoice) return prBillDetails;
    const payment = paymentByVisit?.data;
    if (!payment) return undefined;
    return {
      advancedPaid: payment.advancePayment,
      previousBalance: payment.previousBalance,
    };
  }, [isPRInvoice, prBillDetails, paymentByVisit?.data]);

  const encounterRenderingProviderInfo = useRenderingProviderInfo(
    encounterData?.data
  );
  const renderingProviderInfo = isPRInvoice
    ? prRenderingProviderInfo
    : encounterRenderingProviderInfo;

  return (
    <Flex height={"calc(100vh - 4.4rem)"} p={1.8} gap={0}>
      <Flex
        flexDirection={"column"}
        width={"80%"}
        minHeight={"calc(100vh - 60px)"}
      >
        <Paper
          sx={{
            height: "auto",
            p: 1.2,
            boxSizing: "border-box",
            overflow: "clip",
          }}
        >
          <SuperBillHeader
            patientsData={patientData}
            patientValidationFormik={patientValidationFormik}
            // patientId={patientData?.uuid}
            // setPatientId={setPatientid}
            viewBill={false}
            title={
              isPRInvoice
                ? "Create Invoice for Patient Responsibility"
                : "Create Invoice"
            }
            isVisitRequired={Boolean(invoiceType)}
            invoiceType={invoiceType}
          />
        </Paper>
        <Box p={0.5} height={"auto"} overflow="auto">
          <AddSuperBill
            // Re-mount the form whenever the PR claim changes (or is cleared,
            // e.g. after switching patient) so no CPT row, service detail or
            // balance from the previous claim survives on the new invoice.
            key={
              isPRInvoice ? `pr-claim-${selectedClaimId ?? "none"}` : undefined
            }
            patientValidationFormik={patientValidationFormik}
            patientsData={patientData}
            patientDataBillDetails={billDetails}
            // On PR the CPT rows carry the claim's ERA breakdown, so the visit
            // must not be forwarded — it would re-fetch the encounter codes and
            // overwrite them. The visit still reaches the payload through the
            // claim's `encounter`.
            superBillId={isPRInvoice ? undefined : selectedEncounterId}
            totalAmount={total}
            setTotal={setTotal}
            invoiceType={invoiceType}
            isPatientResponsibilityInvoice={isPRInvoice}
          />
        </Box>
      </Flex>
      <Box width={"20%"} height="inherit">
        <Paper
          sx={{
            height: "inherit",
            p: 1.2,
            boxSizing: "border-box",
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <PatientDetailsSideSection
            patientsData={patientData}
            total={total}
            visitId={selectedEncounterId}
            renderingProviderInfo={renderingProviderInfo}
          />
        </Paper>
      </Box>
    </Flex>
  );
};

export default SuperBillingLayout;
